import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';

export const API_BASE = window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const SALES_REPORT_CACHE_LIMIT = 8;

export const monthNames = {
  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
};

const SALES_REPORT_PARENT_CHANNELS = ['B2B', 'DTC/HCC', 'Internal Company', 'Others', 'Unclassified'];
const SALES_REPORT_HEMA_CHILD_ENTITIES = ['HEMA_EAST', 'HEMA_WEST'];

export const expandSalesReportHemaEntities = (entities = []) => {
  if (!entities.includes('HEMA')) return entities;
  const withoutHema = entities.filter((entity) => entity !== 'HEMA');
  return Array.from(new Set([...withoutHema, ...SALES_REPORT_HEMA_CHILD_ENTITIES]));
};

export const normalizeSalesReportFilterMeta = (data = {}) => {
  const channels = Array.isArray(data.channels) ? data.channels : [];
  const channel2s = Array.isArray(data.channel2s) ? data.channel2s : [];
  const subchannels = Array.isArray(data.subchannels) ? data.subchannels : [];
  const hasParentChannels = channels.some((item) => SALES_REPORT_PARENT_CHANNELS.includes(item));
  const channelsLookLikeSubchannels = channels.some((item) => !SALES_REPORT_PARENT_CHANNELS.includes(item));

  const entities = Array.isArray(data.entities) ? data.entities : [];
  const withHemaEntity = (
    entities.includes('HEMA')
      || !SALES_REPORT_HEMA_CHILD_ENTITIES.every((entity) => entities.includes(entity))
  ) ? entities : [
    ...entities.slice(0, entities.findIndex((entity) => entity === 'HEMA_EAST')),
    'HEMA',
    ...entities.slice(entities.findIndex((entity) => entity === 'HEMA_EAST')),
  ];

  const nextData = withHemaEntity === entities ? data : { ...data, entities: withHemaEntity };

  if (channel2s.length) return nextData;

  if (subchannels.length) {
    return {
      ...nextData,
      channels: hasParentChannels ? channels.filter((item) => SALES_REPORT_PARENT_CHANNELS.includes(item)) : SALES_REPORT_PARENT_CHANNELS,
      channel2s: subchannels,
    };
  }

  if (channels.length && channelsLookLikeSubchannels) {
    return {
      ...nextData,
      channels: SALES_REPORT_PARENT_CHANNELS.filter((item) => item !== 'Unclassified' || channels.includes(item)),
      channel2s: channels,
    };
  }

  return nextData;
};

export const stableSalesReportFilters = (filters = {}) => ({
  reportType: filters.reportType,
  year: filters.year,
  month: filters.month,
  week: filters.week,
  quarter: filters.quarter,
  currency: filters.currency,
  entities: filters.entities || [],
  channels: filters.channels || [],
  channel2s: filters.channel2s || [],
  customerNames: filters.customerNames || [],
  skus: filters.skus || [],
  lines: filters.lines || [],
  regions: filters.regions || [],
  subregions: filters.subregions || [],
  countries: filters.countries || [],
  excludeDistributor: Boolean(filters.excludeDistributor),
  includeOversea: Boolean(filters.includeOversea),
  includeInternal: Boolean(filters.includeInternal),
});

export const isWeeklyReportType = (type) => type === 'weekly' || type === 'weekly2';
export const isMonthlyReportType = (type) => type === 'monthly' || type === 'weeklyFlash';
export const isWeeklyReport2Type = (type) => type === 'weekly2';
export const apiReportType = (type) => {
  if (type === 'weekly2') return 'weekly2-year';
  return isMonthlyReportType(type) ? 'monthly' : 'weekly';
};

export const WEEKLY2_INACTIVE_FILTERS = {
  month: 'All',
  week: 'All',
  channels: [],
  channel2s: [],
  customerNames: [],
  skus: [],
  lines: [],
  regions: [],
  subregions: [],
  countries: [],
};

export const normalizeWeekly2Filters = (filters = {}) => (
  isWeeklyReport2Type(filters.reportType)
    ? { ...filters, ...WEEKLY2_INACTIVE_FILTERS, quarter: filters.quarter || 'All' }
    : filters
);

export const salesReportCacheKey = (filters = {}) => JSON.stringify(stableSalesReportFilters(filters));

export const rememberSalesReport = (cache, key, report) => {
  cache.set(key, report);
  while (cache.size > SALES_REPORT_CACHE_LIMIT) {
    cache.delete(cache.keys().next().value);
  }
};

/* the body a report route is asked with — also what the AI summary sends, so it reads the very report on screen */
export const salesReportRequestBody = (body) => {
  const normalizedBody = normalizeWeekly2Filters(body);
  return {
    ...normalizedBody,
    entities: expandSalesReportHemaEntities(normalizedBody.entities || []),
  };
};

export const fetchSalesReportJson = async (endpoint, body, signal) => {
  const requestBody = salesReportRequestBody(body);
  const response = await fetch(`${API_BASE}/api/sales-report/${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
    signal,
  });
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (parseError) {
    throw new Error(i18n.t('xSales.errParse', { name: endpoint }));
  }
  if (!response.ok) {
    throw new Error(data?.detail || data?.error || i18n.t('xSales.errLoad', { name: endpoint }));
  }
  return data;
};

export const fetchSalesReportVisualHistory = async (body, signal) => {
  const normalizedBody = normalizeWeekly2Filters(body);
  const requestBody = {
    ...normalizedBody,
    entities: expandSalesReportHemaEntities(normalizedBody.entities || []),
  };
  const response = await fetch(`${API_BASE}/api/sales-report/visual-history`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
    signal,
  });
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (parseError) {
    throw new Error('Cannot parse visual history response');
  }
  if (!response.ok) {
    throw new Error(data?.detail || data?.error || 'Cannot load visual history');
  }
  return data;
};

export const isAbortError = (error) => error?.name === 'AbortError';

export const formatNumber = (value, digits = 0) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(number);
};

export const formatMoney = (value, symbol = '') => {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return '-';
  return `${symbol}${formatNumber(number)}`;
};

export const formatPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return `${(number * 100).toFixed(1)}%`;
};

export const growthClass = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || Math.abs(number) < 0.0001) return 'flat';
  return number > 0 ? 'up' : 'down';
};

export const GrowthCell = ({ value }) => {
  const state = growthClass(value);
  const icon = state === 'up' ? '▲' : state === 'down' ? '▼' : '■';
  return <span className={`growth ${state}`}>{icon} {formatPercent(value)}</span>;
};

export const TargetCell = ({ value }) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  const width = Math.max(0, Math.min(100, number * 100));
  return (
    <span className="target-cell">
      <span className="target-bar" style={{ width: `${width}%` }} />
      <span className="target-label">{formatPercent(number).replace(/^\+/, '')}</span>
    </span>
  );
};

// The value is the server's word (Pass / Fail / -) and stays the CSS class; only what is printed is translated.
export const StatusCell = ({ value }) => {
  const { t } = useTranslation();
  const status = String(value || '-');
  const state = status.toLowerCase();
  const shown = status === 'Pass' ? t('xSales.statusPass') : status === 'Fail' ? t('xSales.statusFail') : status;
  return <span className={`status-pill ${state}`}>{shown}</span>;
};

// `label` arrives already translated; the values are filter values (data) and are printed as they are.
export const selectedText = (values, label) => {
  if (!values.length) return i18n.t('xSales.allLabel', { label });
  if (values.length <= 3) return `${label} ${values.join(', ')}`;
  return `${label} ${values.slice(0, 3).join(', ')} +${values.length - 3}`;
};

export const slugPart = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

export const buildExportFileName = (filters, meta) => {
  const reportType = filters.reportType || meta?.reportType || 'weekly';
  const currency = slugPart(meta?.currency || filters.currency || 'krw');
  const year = meta?.year || filters.year || 'year';
  const period = isMonthlyReportType(reportType)
    ? `m${meta?.month || filters.month || 'all'}`
    : slugPart(meta?.week || filters.week || 'all');
  const entityPart = !filters.entities?.length ? 'all_entities' : `${filters.entities.length}_entities`;
  const channelPart = !filters.channels?.length ? 'all_channels' : `${filters.channels.length}_channels`;
  return `sales_report_${reportType}_${year}_${period}_${currency}_${entityPart}_${channelPart}.pdf`;
};
